import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
  FileText,
  Settings2
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import useToast from "../hooks/useToast";
import {
  getMetadataList,
  createMetadata,
  updateMetadata,
  deleteMetadata
} from "../services/metadataService";
import { Select } from "../components/ui/select";

const metadataSchema = z.object({
  page_name: z.string().trim().min(1, "Page name is required"),
  meta_title: z.string().trim().optional(),
  meta_description: z.string().trim().optional(),
  keywords: z.string().trim().optional()
});

const defaultValues = {
  page_name: "",
  meta_title: "",
  meta_description: "",
  keywords: ""
};

const STATIC_PAGES = [
  { value: "home", label: "Home" },
  { value: "about", label: "About Us" },
  { value: "contact", label: "Contact Us" },
  { value: "doctors", label: "Doctors" },
  { value: "gallery", label: "Gallery" },
  { value: "courses", label: "Courses" }
];

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }
  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function MetadataManagement() {
  const [metadataList, setMetadataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(metadataSchema)
  });

  async function loadMetadata(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getMetadataList({ page: pageArg, limit: limitArg });

    if (response?.success) {
      setMetadataList(response.data.metadata || []);
      setPagination({
        page: response.data.page || pageArg,
        limit: response.data.limit || limitArg,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load metadata");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadMetadata(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadMetadata(targetPage, targetLimit);
  };

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (meta) => {
    setEditingId(meta.id);
    reset({
      page_name: meta.page_name,
      meta_title: meta.meta_title || "",
      meta_description: meta.meta_description || "",
      keywords: meta.keywords || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    const response = await deleteMetadata(pendingDelete.id);
    setIsDeleting(false);

    if (response?.success) {
      showSuccessToast(response.message || "Metadata deleted successfully");
      setPendingDelete(null);
      const nextPage = metadataList.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
      refreshListing(nextPage, pagination.limit);
    } else {
      showErrorToast(response?.message || "Unable to delete metadata");
    }
  };

  const onSubmit = async (values) => {
    const response = editingId
      ? await updateMetadata(editingId, values)
      : await createMetadata(values);

    if (response?.success) {
      showSuccessToast(response.message || "Metadata saved successfully");
      setIsModalOpen(false);
      refreshListing(editingId ? pagination.page : 1, pagination.limit);
    } else {
      showErrorToast(response?.message || "Unable to save metadata");
    }
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SEO Settings"
        title="Dynamic Metadata"
        description="Manage page-specific meta titles, descriptions, and keywords."
        badge={`${pagination.total} Records`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Metadata Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              Configure SEO settings for different pages of your website.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Search className="h-5 w-5" />
            </div>
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add Metadata
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading metadata records...
            </div>
          ) : metadataList.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                        <thead className="bg-slate-50/90">
                            <tr className="text-left">
                                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Page Name</th>
                                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Meta Title</th>
                                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Description</th>
                                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white">
                            {metadataList.map((item) => (
                                <tr key={item.id} className="border-t border-slate-100 align-top">
                                    <td className="px-5 py-4 text-sm font-semibold text-panel-ink capitalize">{item.page_name}</td>
                                    <td className="px-5 py-4 text-sm text-primary">{item.meta_title || "--"}</td>
                                    <td className="px-5 py-4 text-sm leading-6 text-panel-muted max-w-xs truncate">
                                        {item.meta_description || "--"}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex gap-2">
                                            <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(item)}>
                                                <Pencil className="mr-2 h-4 w-4" />
                                                Edit
                                            </Button>
                                            <Button type="button" size="sm" variant="ghost" className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted" onClick={() => setPendingDelete(item)}>
                                                <Trash2 className="mr-2 h-4 w-4" />
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                  <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                  <span className="font-semibold text-panel-ink">{pagination.total}</span> records
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(curr => ({ ...curr, page: curr.page - 1 }))}
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Prev
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                      {pageNumbers.map((num, i) => (
                        num === "..." ? (
                           <span key={`ell-${i}`} className="px-1 text-sm font-medium text-panel-muted">...</span>
                        ) : (
                          <Button
                            key={num}
                            type="button"
                            size="sm"
                            variant={pagination.page === num ? "default" : "outline"}
                            onClick={() => setPagination(curr => ({ ...curr, page: num }))}
                          >
                            {num}
                          </Button>
                        )
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPagination(curr => ({ ...curr, page: curr.page + 1 }))}
                    >
                      Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FileText className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No metadata records found</p>
              <p className="mt-2 text-sm text-panel-muted">
                Start by adding metadata for your website pages like 'home', 'about-us', etc.
              </p>
              <Button type="button" className="mt-5" onClick={openCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                Add Metadata
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="my-auto w-full max-w-2xl rounded-[32px] border border-white/80 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-6 py-5 backdrop-blur rounded-t-[32px]">
              <div>
                <h2 className="text-2xl font-bold text-panel-ink">
                  {editingId ? "Edit Metadata" : "Add Metadata"}
                </h2>
                <p className="mt-1 text-sm text-panel-muted">
                  Configure SEO tags for this page.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Settings2 className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-panel-ink">SEO Details</h3>
                    </div>
                    
                    <div className="space-y-2">
                        <FieldLabel>Page Name</FieldLabel>
                        <Select {...register("page_name")}>
                            <option value="" disabled>Select a page...</option>
                            {STATIC_PAGES.map(page => (
                                <option key={page.value} value={page.value}>{page.label}</option>
                            ))}
                            {editingId && metadataList.find(m => m.id === editingId)?.page_name && !STATIC_PAGES.some(p => p.value === metadataList.find(m => m.id === editingId)?.page_name) && (
                                <option value={metadataList.find(m => m.id === editingId)?.page_name}>
                                    {metadataList.find(m => m.id === editingId)?.page_name} (Current)
                                </option>
                            )}
                        </Select>
                        {errors.page_name && <FieldError>{errors.page_name.message}</FieldError>}
                    </div>

                    <div className="space-y-2">
                        <FieldLabel>Meta Title</FieldLabel>
                        <Input placeholder="Search engine title" {...register("meta_title")} />
                    </div>

                    <div className="space-y-2">
                        <FieldLabel>Meta Description</FieldLabel>
                        <Textarea placeholder="Short summary for search results" {...register("meta_description")} className="min-h-[100px]" />
                    </div>

                    <div className="space-y-2">
                        <FieldLabel>Keywords</FieldLabel>
                        <Input placeholder="keyword1, keyword2" {...register("keywords")} />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
                    <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : editingId ? "Update Metadata" : "Create Metadata"}
                    </Button>
                </div>
            </form>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border border-white/80 bg-white p-6 shadow-2xl">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-panel-ink">Delete Metadata</h2>
            <p className="mt-3 text-sm leading-6 text-panel-muted">
              Are you sure you want to delete SEO settings for <span className="font-semibold text-panel-ink capitalize">{pendingDelete.page_name}</span>?
            </p>
            <div className="mt-6 flex gap-3">
              <Button type="button" variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete Permanently"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setPendingDelete(null)} disabled={isDeleting}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
